import React from 'react';
import { BOOK, bookJsonLd } from '../content/book';
import {
  PageHead, Section, Eyebrow, PageTitle, Lede, SectionHead, Button, Card, CardTitle, CardBody, Grid, Saying, CONTACT_MAILTO,
} from '../components/ui';

/** The Book. Cover, description, formats with ISBNs, what is inside, the author. */

const BookPage = () => (
  <>
    <PageHead
      title={`${BOOK.title}: ${BOOK.subtitle}`}
      description={`${BOOK.descriptionLine} By ${BOOK.author}. Hardcover, paperback, and Kindle on Amazon from ${BOOK.publishedText}.`}
      path="/book"
      image={BOOK.og}
      jsonLd={bookJsonLd()}
    />

    <Section tone="page" reveal={false}>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-start">
        <div className="md:col-span-4 lg:col-span-4">
          <img src={BOOK.cover} alt={`${BOOK.title}: ${BOOK.subtitle}, by ${BOOK.author}`} width="704" height="1106"
            className="w-full max-w-[220px] sm:max-w-[360px] mx-auto md:mx-0 h-auto rounded-md border border-navy-200 dark:border-navy-700 shadow-[0_18px_40px_-24px_rgba(14,22,38,0.45)]" />
        </div>
        <div className="md:col-span-8">
          <Eyebrow>The book. Out {BOOK.publishedText}.</Eyebrow>
          <PageTitle>{BOOK.title}</PageTitle>
          <p className="font-display text-subhead mt-2 text-emerald-600 dark:text-emerald-300">{BOOK.subtitle}</p>
          <Lede>{BOOK.backCoverLine} {BOOK.descriptionLine}</Lede>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-[14px]">
            {BOOK.formats.map((f, i) => (
              <a key={f.name} href={f.url} target="_blank" rel="noopener noreferrer"
                className={[
                  'rounded-lg border p-4 flex flex-col gap-1 transition-colors',
                  i === 0 ? 'bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600' : 'bg-white dark:bg-navy-850 border-navy-200 dark:border-navy-700 hover:border-emerald-500 text-navy-800 dark:text-white',
                ].join(' ')}>
                <span className="font-sans font-semibold text-[17px]">{f.name}</span>
                <span className="font-sans text-interface opacity-90">{f.price} on Amazon</span>
                <span className={['font-mono text-meta mt-1', i === 0 ? 'opacity-80' : 'text-navy-500 dark:text-navy-400'].join(' ')}>ISBN {f.isbn}</span>
              </a>
            ))}
          </div>
          <p className="font-sans text-interface mt-4 text-navy-500 dark:text-navy-400">{BOOK.publisher}. {BOOK.pages} pages, {BOOK.trim}.</p>
        </div>
      </div>
    </Section>

    <Section tone="card" id="about-the-book">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-7">
          <Eyebrow>About the book</Eyebrow>
          <SectionHead>Thinking with AI is a different discipline from using it.</SectionHead>
          <div className="prose-rbl mt-5">
            {BOOK.description.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
          </div>
        </div>
        <div className="md:col-span-5">
          <Eyebrow>Inside</Eyebrow>
          <div className="flex flex-col gap-[14px]">
            {BOOK.inside.map((x) => (
              <Card key={x.head} accent>
                <CardTitle>{x.head}</CardTitle>
                <CardBody>{x.body}</CardBody>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </Section>

    <Section tone="page">
      <Saying tag={BOOK.title}>The real danger with AI is not what it does. It is what it lets you not do.</Saying>
    </Section>

    <Section tone="card" id="author">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-4">
          <img src="/images/v2/hero-session-520.jpg" alt="Srini Koushik" width="520" height="650" loading="lazy" className="w-full max-w-[360px] h-auto rounded-md border border-navy-200 dark:border-navy-700" />
        </div>
        <div className="md:col-span-8">
          <Eyebrow>The author</Eyebrow>
          <SectionHead>{BOOK.author}</SectionHead>
          <p className="prose-rbl mt-5">{BOOK.authorBio}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button href="https://www.srinikoushik.com" variant="secondary">srinikoushik.com</Button>
            <Button href={CONTACT_MAILTO} variant="secondary">Book Srini to speak</Button>
          </div>
        </div>
      </div>
    </Section>

    <Section tone="page" id="bulk">
      <Grid cols={2}>
        <Card>
          <CardTitle>For your leadership team</CardTitle>
          <CardBody>Copies for a cohort, a board, or an executive team, with a working session built around the book. Ask about bulk orders and a Thinking with AI session.</CardBody>
          <div className="mt-4"><Button href={CONTACT_MAILTO} variant="secondary">Ask about a session</Button></div>
        </Card>
        <Card>
          <CardTitle>Find your stage first</CardTitle>
          <CardBody>The book teaches the discipline. The Compass tells you where you stand on the AI Fluency Ladder before you start. Free, ten minutes.</CardBody>
          <div className="mt-4"><Button to="/compass" variant="secondary">Take the Compass</Button></div>
        </Card>
      </Grid>
    </Section>
  </>
);

export default BookPage;
